import { EditIcon, IconButton, Popover, Rectangle, SearchInput, Skeleton, showToast } from '@lokalise/louis';
import { useMemo, useState } from 'react';

import type { Language } from '@app/api/styleGuide';
import { randomNumberFromRange } from '@app/utils/number';

import { useStyleGuideContext } from '../../StyleGuideContext';
import {
    AssignButton,
    AssignButtonContainer,
    MenuContainer,
    MenuListContainer,
    NoResults,
    SearchContainer,
} from '../../styles';
import OverflowTextTooltip from '../OverflowTextTooltip/OverflowTextTooltip';
import { MenuItem } from './styles';

type LanguageSelectorProps = {
    assignedLanguage: Language | null;
    styleGuideId: string;
};

export const LanguageSelector = ({ assignedLanguage, styleGuideId }: LanguageSelectorProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { languages, saveStyleGuides } = useStyleGuideContext();
    const [searchValue, setSearchValue] = useState('');

    const filteredLanguages = useMemo(
        () => languages.filter(({ label }) => label.toLowerCase().includes(searchValue.toLowerCase())),
        [languages, searchValue],
    );

    const onLanguageSelect = async (languageId: number) => {
        setIsLoading(true);
        const response = await saveStyleGuides([{ id: styleGuideId, languageId }]);

        if ('error' in response) {
            showToast({ type: 'error', title: 'Error while saving language. Please try again.' });
        }

        setIsLoading(false);
    };

    return (
        <Popover
            placement="bottom-start"
            role="menu"
            content={
                <MenuContainer>
                    <SearchContainer>
                        <SearchInput
                            autoFocus
                            disabled={isLoading}
                            value={searchValue}
                            debounceTime={0}
                            placeholder="Search language"
                            onValueChange={(value) => setSearchValue(value)}
                        />
                    </SearchContainer>

                    <MenuListContainer>
                        {filteredLanguages.map(({ id, label, isoCode }) => (
                            <MenuItem
                                disabled={isLoading}
                                key={id}
                                onClick={() => onLanguageSelect(id)}
                                selected={id === assignedLanguage?.id && !isLoading}
                            >
                                {isLoading ? (
                                    <Skeleton animated description="Languages loading">
                                        <Rectangle key={id} height={20} width={`${randomNumberFromRange(40, 80)}%`} />
                                    </Skeleton>
                                ) : (
                                    <span>
                                        {label} {isoCode !== '' && `(${isoCode})`}
                                    </span>
                                )}
                            </MenuItem>
                        ))}
                        {filteredLanguages.length === 0 && (
                            <NoResults>
                                {searchValue ? (
                                    <p>
                                        No match found for <strong>{searchValue}</strong>
                                    </p>
                                ) : (
                                    <p>No languages</p>
                                )}
                            </NoResults>
                        )}
                    </MenuListContainer>
                </MenuContainer>
            }
        >
            {assignedLanguage ? (
                <AssignButtonContainer>
                    <OverflowTextTooltip>
                        {assignedLanguage.label} {assignedLanguage.isoCode !== '' && `(${assignedLanguage.isoCode})`}
                    </OverflowTextTooltip>
                    <IconButton appearance="secondary" ariaLabel="Edit language">
                        <EditIcon />
                    </IconButton>
                </AssignButtonContainer>
            ) : (
                <AssignButton variant="tertiary">Assign language</AssignButton>
            )}
        </Popover>
    );
};

export default LanguageSelector;
